// ═══════════════════════════════════════════════════════════════
// SCHOLARSHIP REPORT TAB — Donors → Scholarships → Students
// Path: src/components/scholarships/ScholarshipReportTab.jsx
//
// Dual-sided report by season:
//   Left: Gifts received per scholarship (incoming)
//   Right: Awards disbursed per scholarship (outgoing)
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Printer, DollarSign, Award, Users } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { ActionButton } from "../ui/ActionButton";
import { supabase } from "../../lib/supabase";
import {
  scholarshipsService,
  scholarshipAwardsService,
  getCurrentSeason,
  getSeasonOptions,
  getSeasonDates,
} from "../../services/scholarshipService";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const ScholarshipReportTab = () => {
  const { palette, members, showLoading, hideLoading } = useApp();

  const [season, setSeason] = useState(getCurrentSeason());
  const [scholarships, setScholarships] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [awards, setAwards] = useState([]);
  const seasonOptions = getSeasonOptions();

  // ── Load data ───────────────────────────────────────────────
  const loadData = useCallback(async () => {
    showLoading("Loading scholarship report...");
    try {
      const { start, end } = getSeasonDates(season);

      const [scholarshipData, awardData] = await Promise.all([
        scholarshipsService.getAll(),
        scholarshipAwardsService.getBySeason(season),
      ]);

      // Fetch gifts linked to scholarships in this season's date range
      const allGifts = [];
      const PAGE_SIZE = 1000;
      let offset = 0;
      while (true) {
        const { data, error } = await supabase
          .from("gifts")
          .select("id, member_id, amount, gift_date, scholarship_id")
          .not("scholarship_id", "is", null)
          .gte("gift_date", start)
          .lte("gift_date", end)
          .range(offset, offset + PAGE_SIZE - 1);

        if (error || !data) break;
        allGifts.push(...data);
        if (data.length < PAGE_SIZE) break;
        offset += PAGE_SIZE;
      }

      setScholarships(scholarshipData);
      setGifts(allGifts);
      setAwards(awardData);
    } catch (err) {
      // silent
    } finally {
      hideLoading();
    }
  }, [season, showLoading, hideLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Group data by scholarship ───────────────────────────────
  const reportData = useMemo(() => {
    return scholarships
      .filter((s) => s.is_active)
      .map((s) => {
        const scholarshipGifts = gifts.filter((g) => g.scholarship_id === s.id);
        const scholarshipAwards = awards.filter((a) => a.scholarship_id === s.id);

        const totalReceived = scholarshipGifts.reduce((sum, g) => sum + (Number(g.amount) || 0), 0);
        const totalDisbursed = scholarshipAwards.reduce((sum, a) => sum + (Number(a.award_amount) || 0), 0);

        // Enrich gifts with donor names
        const enrichedGifts = scholarshipGifts.map((g) => {
          const member = members.find((m) => m.id === g.member_id);
          return {
            ...g,
            donorName: member
              ? member.credentialedFullName || `${member.firstName || ""} ${member.lastName || ""}`.trim() || "Unknown"
              : "Unknown",
          };
        });

        return {
          scholarship: s,
          gifts: enrichedGifts,
          awards: scholarshipAwards,
          totalReceived,
          totalDisbursed,
        };
      })
      .filter((r) => r.gifts.length > 0 || r.awards.length > 0);
  }, [scholarships, gifts, awards, members]);

  // ── Grand totals ────────────────────────────────────────────
  const grandReceived = reportData.reduce((s, r) => s + r.totalReceived, 0);
  const grandDisbursed = reportData.reduce((s, r) => s + r.totalDisbursed, 0);
  const totalDonors = new Set(gifts.map((g) => g.member_id)).size;
  const totalStudents = new Set(awards.map((a) => a.student_name)).size;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <label style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY }}>
          Season:
        </label>
        <select
          value={season}
          onChange={(e) => setSeason(e.target.value)}
          style={{
            padding: "6px 10px", borderRadius: 6,
            fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
            border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none",
          }}
        >
          {seasonOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <ActionButton label="Print" icon={Printer} variant="secondary" small onClick={() => window.print()} />
      </div>

      {/* Summary tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <SummaryTile icon={DollarSign} label="Received" value={fmtCurrency(grandReceived)} color="#10b981" palette={palette} />
        <SummaryTile icon={Award} label="Disbursed" value={fmtCurrency(grandDisbursed)} color="#f59e0b" palette={palette} />
        <SummaryTile icon={Users} label="Donors" value={totalDonors} color="#3b82f6" palette={palette} />
        <SummaryTile icon={Users} label="Students" value={totalStudents} color="#8b5cf6" palette={palette} />
      </div>

      {/* Report body — one section per scholarship */}
      {reportData.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY }}>
          No scholarship activity in {season}.
        </div>
      ) : (
        reportData.map((row) => (
          <div
            key={row.scholarship.id}
            style={{
              background: palette.cardBg,
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            {/* Scholarship header */}
            <div
              style={{
                padding: "12px 16px",
                borderBottom: `1px solid ${palette.cardBorder}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {row.scholarship.name}
              </span>
              <div style={{ display: "flex", gap: 16, fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY }}>
                <span>
                  <span style={{ color: palette.text3 }}>In: </span>
                  <span style={{ fontWeight: 700, color: "#10b981" }}>{fmtCurrency(row.totalReceived)}</span>
                </span>
                <span>
                  <span style={{ color: palette.text3 }}>Out: </span>
                  <span style={{ fontWeight: 700, color: "#f59e0b" }}>{fmtCurrency(row.totalDisbursed)}</span>
                </span>
              </div>
            </div>

            {/* Two columns: Donors | Students */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
              {/* Incoming — Donors */}
              <div style={{ borderRight: `1px solid ${palette.cardBorder}` }}>
                <div style={{ padding: "8px 16px", borderBottom: `1px solid ${palette.cardBorder}`, fontSize: "0.6rem", fontWeight: 600, color: "#10b981", fontFamily: FONT_FAMILY }}>
                  Gifts Received ({row.gifts.length})
                </div>
                {row.gifts.length === 0 ? (
                  <div style={{ padding: 16, fontSize: "0.6rem", color: palette.text3, fontFamily: FONT_FAMILY, fontStyle: "italic" }}>
                    No gifts linked to this scholarship in {season}.
                  </div>
                ) : (
                  row.gifts.map((g) => (
                    <div
                      key={g.id}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "6px 16px", borderBottom: `1px solid ${palette.cardBorder}`,
                      }}
                    >
                      <span style={{ fontSize: FONT_SIZES.xs, color: palette.text1, fontFamily: FONT_FAMILY }}>
                        {g.donorName}
                      </span>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: "#10b981", fontFamily: FONT_FAMILY }}>
                          {fmtCurrency(g.amount)}
                        </span>
                        <span style={{ fontSize: "0.55rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
                          {fmtDate(g.gift_date)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Outgoing — Students */}
              <div>
                <div style={{ padding: "8px 16px", borderBottom: `1px solid ${palette.cardBorder}`, fontSize: "0.6rem", fontWeight: 600, color: "#f59e0b", fontFamily: FONT_FAMILY }}>
                  Awards Disbursed ({row.awards.length})
                </div>
                {row.awards.length === 0 ? (
                  <div style={{ padding: 16, fontSize: "0.6rem", color: palette.text3, fontFamily: FONT_FAMILY, fontStyle: "italic" }}>
                    No awards in {season}.
                  </div>
                ) : (
                  row.awards.map((a) => (
                    <div
                      key={a.id}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "6px 16px", borderBottom: `1px solid ${palette.cardBorder}`,
                      }}
                    >
                      <span style={{ fontSize: FONT_SIZES.xs, color: palette.text1, fontFamily: FONT_FAMILY }}>
                        {a.student_name}
                      </span>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: "#f59e0b", fontFamily: FONT_FAMILY }}>
                          {fmtCurrency(a.award_amount)}
                        </span>
                        <span style={{ fontSize: "0.55rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
                          {fmtDate(a.award_date)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ── Summary Tile ──────────────────────────────────────────────
const SummaryTile = ({ icon: Icon, label, value, color, palette }) => (
  <div
    style={{
      background: palette.cardBg,
      border: `1px solid ${palette.cardBorder}`,
      borderRadius: 10,
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 10,
    }}
  >
    <div
      style={{
        width: 32, height: 32, borderRadius: 8,
        background: `${color}15`,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}
    >
      <Icon size={ICON_SIZES.sm} style={{ color }} />
    </div>
    <div>
      <div style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
        {value}
      </div>
      <div style={{ fontSize: "0.55rem", color: palette.text3, fontFamily: FONT_FAMILY }}>{label}</div>
    </div>
  </div>
);
